/**
 * Leveranseprofil: kundens innstillinger for hvilke funn som leveres (motoren skriver `leveranseprofil.json`, det
 * lokale API-et legger den i prosjekt-JSON som `leveranseprofil`, se backend/local-api/README.md).
 *
 * Standard (uten profil): bare trær som treffer linja, master som heller mer enn 10°, brudd på minsteavstand.
 * Bakkeavstand er bare til orientering til et krav er satt. Stasjoner, isolatorer, barduner og traverser er bare
 * klassifisering, og usikre kandidater og støy vises aldri for kunden.
 *
 * Rene funksjoner (tests/unit/leveranse.test.ts). Importerer ikke featureStyle.ts (som bruker denne modulen).
 */

export type FallModus = 'treffer_linja' | 'innenfor_krav';

export interface Leveranseprofil {
  kunde: string | null;
  fallrisiko: { vis: boolean; modus: FallModus; over_linja_m: number | null };
  skjeve_master: { vis: boolean; max_helning_grader: number };
  minsteavstand: { vis: boolean };
  bakkeavstand: { vis: boolean; krav_m: number | null };
  objektavstand: { vis: boolean; krav_m: number | null };
  /** mastens helning fra motoren (leveransepakkens Master.gpkg), per `support_id`; går foran viewerens utregning */
  helning_per_mast?: Record<string, number>;
  /** hvor profilen kom fra (API-et): leveransepakke, classify-mappen eller innebygd standard */
  kilde: 'leveransepakke' | 'classify' | 'standard';
  fil: string | null;
}

export const STANDARD_PROFIL: Readonly<Leveranseprofil> = Object.freeze({
  kunde: null,
  fallrisiko: { vis: true, modus: 'treffer_linja', over_linja_m: null },
  skjeve_master: { vis: true, max_helning_grader: 10 },
  minsteavstand: { vis: true },
  bakkeavstand: { vis: true, krav_m: null },
  objektavstand: { vis: true, krav_m: null },
  kilde: 'standard',
  fil: null,
} as Leveranseprofil);

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const numIn = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi ? v : undefined;
const bool = (v: unknown, std: boolean) => (typeof v === 'boolean' ? v : std);

function engineTilts(v: unknown): Record<string, number> | undefined {
  const out: Record<string, number> = {};
  for (const [k, t] of Object.entries(obj(v))) { const n = numIn(t, 0, 90); if (n !== undefined) out[k] = n; }
  return Object.keys(out).length ? out : undefined;
}

/** `leveranseprofil` fra prosjekt-JSON → profil med standardverdier (ugyldige verdier gir standard). */
export function parseLeveranseprofil(raw: unknown): Leveranseprofil {
  const r = obj(raw), s = STANDARD_PROFIL;
  const fr = obj(r.fallrisiko), sm = obj(r.skjeve_master), mi = obj(r.minsteavstand), ba = obj(r.bakkeavstand), ob = obj(r.objektavstand);
  const modus = String(fr.modus ?? fr.mode ?? '').trim().toLowerCase();
  const kilde = r.kilde === 'leveransepakke' || r.kilde === 'classify' ? r.kilde : 'standard';
  return {
    kunde: typeof r.kunde === 'string' && r.kunde.trim() ? r.kunde.trim() : null,
    fallrisiko: { vis: bool(fr.vis, true), modus: modus === 'innenfor_krav' ? 'innenfor_krav' : 'treffer_linja',
      over_linja_m: numIn(fr.over_linja_m ?? fr.min_over_linja_m, 0, 100) ?? null },
    skjeve_master: { vis: bool(sm.vis, true), max_helning_grader: numIn(sm.max_helning_grader, 0, 90) ?? s.skjeve_master.max_helning_grader },
    minsteavstand: { vis: bool(mi.vis, true) },
    bakkeavstand: { vis: bool(ba.vis, true), krav_m: numIn(ba.krav_m, 0, 200) ?? null },
    objektavstand: { vis: bool(ob.vis, true), krav_m: numIn(ob.krav_m, 0, 200) ?? null },
    kilde,
    fil: typeof r.fil === 'string' && r.fil ? r.fil : null,
    ...(engineTilts(r.helning_per_mast) ? { helning_per_mast: engineTilts(r.helning_per_mast) } : {}),
  };
}

/**
 * Kundevisning (Resultat + «Som kunden ser det»): featureStyle skjuler det profilen ikke tar med, så tegningen og
 * tallene i panelet stemmer. `profil` brukes også i full visning (terskel for skjeve master).
 */
export const leveranseView: { customer: boolean; profil: Leveranseprofil } = { customer: false, profil: { ...STANDARD_PROFIL } };
export function setLeveranseView(customer: boolean, profil?: Leveranseprofil): void {
  leveranseView.customer = customer;
  if (profil) leveranseView.profil = profil;
}

// ------------------------------------------------------------------ hjelpere

const flag = (v: unknown): boolean | undefined => (v === true || v === 1 || v === '1' ? true : v === false || v === 0 || v === '0' ? false : undefined);
export const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

// ------------------------------------------------------------------ fallrisiko

/**
 * Tas treet med for kunden? `treffer_linja`: bare trær som treffer linja (`hits_geometric`). `innenfor_krav`: også trær
 * som kommer innenfor minsteavstanden (`hits`). `over_linja_m`: treff må nå minst så langt over linja.
 * Feltene må være normalisert (normalizeFallProps).
 */
export function fallIncluded(p: Record<string, unknown>, profil: Leveranseprofil): boolean {
  if (!profil.fallrisiko.vis) return false;
  const geo = !!flag(p.hits_geometric);
  if (geo) {
    const min = profil.fallrisiko.over_linja_m;
    return min === null || (num(p.overshoot_m) ?? 0) >= min;
  }
  return profil.fallrisiko.modus === 'innenfor_krav' && !!flag(p.hits);
}

// ------------------------------------------------------------------ minsteavstand og bakkeavstand

export const clearanceViolation = (p: Record<string, unknown>): boolean => (num(p.deficit_m) ?? 0) > 0;

/**
 * Krav for en bakke-/objektavstand: profilens krav, ellers motorens (`krav_m` når `brudd` er 0/1). undefined = ingen
 * krav, og målingen er bare til orientering.
 */
export function distanceRequirement(p: Record<string, unknown>, profilKrav: number | null): number | undefined {
  if (profilKrav !== null) return profilKrav;
  const b = num(p.brudd);
  return b === 0 || b === 1 ? num(p.krav_m) : undefined;
}

/** Brudd: under profilens krav, eller motorens `brudd = 1` når profilen ikke har krav. */
export function distanceViolation(p: Record<string, unknown>, key: 'min_hoyde_m' | 'avstand_m', profilKrav: number | null): boolean {
  if (profilKrav !== null) { const v = num(p[key]); return v !== undefined && v < profilKrav; }
  return num(p.brudd) === 1;
}

// ------------------------------------------------------------------ skjeve master

export const TILT_LAYER = 'support_poles', TILT_AXES_LAYER = 'support_pole_axes';
export type TiltMethod = 'motoren' | 'stolpen' | 'stolpen som heller mest' | 'mastens akse' | 'snitt av bena';
interface MastTilt { supportId: string; type: string; tilt: number | undefined; method: TiltMethod; legs: number }
const tiltRep = new WeakMap<object, MastTilt>();
const tiltPrepared = new WeakSet<object>();
type TiltFeature = { props: Record<string, unknown>; parts?: number[][][] };
let lastPoles: TiltFeature[] | null = null;
/** stolpeakser (fot -> topp, med z) per `pole_id` fra `support_pole_axes` */
const axesByPole = new Map<string, number[][]>();

/** Skrevet til punktskyen? `written_to_pointcloud`, ellers status VALIDATED. */
export function isWrittenPole(p: Record<string, unknown>): boolean {
  const w = flag(p.written_to_pointcloud) ?? flag(p.written_flag);
  if (w !== undefined) return w;
  return typeof p.status === 'string' && p.status.trim().toUpperCase() === 'VALIDATED';
}

const sidOf = (p: Record<string, unknown>, i: number) => (p.support_id !== undefined && p.support_id !== null && p.support_id !== '' ? String(p.support_id) : `#${i}`);

/**
 * Mastens helning, samme metode som motorens leveransepakke (delivery/findings.py `_structure_tilt`):
 * enkeltstolpe = stolpens `tilt_deg`; dobbeltstolpe/H-mast = stolpen som heller mest; flerbent mast (MULTI_LEG) = aksen
 * fra snittet av stolpefotene til snittet av toppene (bena i A-master heller innover med vilje), ellers snittet av bena.
 * `axes`: stolpeaksene (fot -> topp, 3D) per `pole_id`.
 */
export function structureTilt(type: string, poles: Record<string, unknown>[], axes: ReadonlyMap<string, number[][]>): { tilt: number | undefined; method: TiltMethod } {
  const tilts = poles.map((p) => num(p.tilt_deg)).filter((t): t is number => t !== undefined);
  if (!tilts.length) return { tilt: undefined, method: 'stolpen' };
  const t = type.trim().toUpperCase();
  if (t === 'SINGLE_POLE' || poles.length === 1) return { tilt: tilts[0], method: 'stolpen' };
  if (t === 'MULTI_LEG') {
    const segs = poles.map((p) => axes.get(String(p.pole_id ?? ''))).filter((c): c is number[][] => !!c && c.length >= 2 && c.every((v) => Number.isFinite(v[2])));
    if (segs.length === poles.length) {
      const mean = (end: boolean, j: number) => segs.reduce((s, c) => s + c[end ? c.length - 1 : 0][j], 0) / segs.length;
      const v = [0, 1, 2].map((j) => mean(true, j) - mean(false, j));
      if (v[2] > 0) return { tilt: (Math.atan2(Math.hypot(v[0], v[1]), v[2]) * 180) / Math.PI, method: 'mastens akse' };
    }
    return { tilt: tilts.reduce((a, b) => a + b, 0) / tilts.length, method: 'snitt av bena' };
  }
  return { tilt: Math.max(...tilts), method: 'stolpen som heller mest' };
}

/**
 * Kalles når `support_poles` eller `support_pole_axes` er lest: én representant per klassifisert mast (`support_id`,
 * minst én stolpe skrevet til punktskyen) - stolpen som heller mest, der markøren tegnes. Helningen er mastens
 * (structureTilt). Returnerer true når stolpelaget må tegnes på nytt (aksene kom etter stolpene).
 */
export function prepareTiltLayer(layerId: string, features: TiltFeature[]): boolean {
  if (layerId === TILT_AXES_LAYER) {
    axesByPole.clear();
    for (const f of features) {
      const id = f.props.pole_id;
      if (id !== undefined && id !== null && id !== '' && f.parts?.[0]?.length) axesByPole.set(String(id), f.parts[0]);
    }
    if (!lastPoles) return false;
    features = lastPoles;
  } else if (layerId === TILT_LAYER) lastPoles = features;
  else return false;
  const groups = new Map<string, TiltFeature[]>();
  features.forEach((f, i) => { tiltPrepared.add(f.props); const sid = sidOf(f.props, i); groups.set(sid, [...(groups.get(sid) ?? []), f]); });
  for (const [sid, fs] of groups) {
    for (const f of fs) tiltRep.delete(f.props);
    if (!fs.some((f) => isWrittenPole(f.props))) continue;
    const props = fs.map((f) => f.props);
    const type = String(props[0].support_type ?? '');
    const { tilt, method } = structureTilt(type, props, axesByPole);
    const rep = fs.reduce((a, b) => ((num(b.props.tilt_deg) ?? -1) > (num(a.props.tilt_deg) ?? -1) ? b : a));
    tiltRep.set(rep.props, { supportId: sid, type, tilt, method, legs: fs.length });
  }
  return layerId === TILT_AXES_LAYER;
}

/**
 * Mastens helning når objektet er representanten for en klassifisert mast; ellers undefined. Motorens verdi
 * (leveransepakkens Master.gpkg -> `helning_per_mast`) går foran viewerens egen utregning.
 */
export function poleTilt(p: Record<string, unknown>, profil: Leveranseprofil = leveranseView.profil): number | undefined {
  if (!tiltPrepared.has(p)) return isWrittenPole(p) ? num(p.tilt_deg) : undefined; // ikke forberedt (f.eks. i tester): stolpen alene
  const r = tiltRep.get(p);
  if (!r) return undefined;
  return profil.helning_per_mast?.[r.supportId] ?? r.tilt;
}
export function poleTiltMethod(p: Record<string, unknown>, profil: Leveranseprofil = leveranseView.profil): TiltMethod | undefined {
  const r = tiltRep.get(p);
  if (!r) return undefined;
  return profil.helning_per_mast?.[r.supportId] !== undefined ? 'motoren' : r.method;
}
export const poleLegs = (p: Record<string, unknown>): number => tiltRep.get(p)?.legs ?? 1;

export function isTiltFinding(p: Record<string, unknown>, profil: Leveranseprofil): boolean {
  if (!profil.skjeve_master.vis) return false;
  const t = poleTilt(p, profil);
  return t !== undefined && t > profil.skjeve_master.max_helning_grader;
}

/** «12°» (hele grader fra 10°, ellers én desimal). */
export const degText = (t: number): string => `${t.toLocaleString('nb-NO', { maximumFractionDigits: t >= 10 ? 0 : 1 })}°`;
export const tiltLabel = (t: number): string => `Masten heller ${degText(t)}`;
